// Death and birth notification (docs/plans/phase2-mortuary-and-notifications.md §3.2). Causes,
// notes, names and weights travel in request bodies only; addresses carry internal IDs.
import { queryOptions } from '@tanstack/react-query';
import {
  birthFormOptionsSchema,
  birthListSchema,
  birthNotificationSchema,
  birthPrintSchema,
  deathFormOptionsSchema,
  deathVisitFormSchema,
  deathListSchema,
  deathPrintSchema,
  deathRecordSchema,
  patientBirthsSchema,
  patientDeathsSchema,
  printCopySchema,
  type recordBirthBodySchema,
  type recordDeathBodySchema,
} from '@wodi/shared';
import type { z } from 'zod';

import { api } from './client.ts';

type In<T extends z.ZodType> = z.input<T>;

// --- Deaths ------------------------------------------------------------------------------

export const deathFormOptionsQuery = queryOptions({
  queryKey: ['deaths', 'form-options'],
  queryFn: () => api('GET', '/deaths/form-options', { schema: deathFormOptionsSchema }),
  staleTime: 5 * 60_000,
});

/** Whether a death can be recorded on this visit, and the record it would replace (D14). */
export const deathVisitFormQuery = (visitId: string) =>
  queryOptions({
    queryKey: ['deaths', 'visit-form', visitId],
    queryFn: () => api('GET', `/visits/${visitId}/death-form`, { schema: deathVisitFormSchema }),
  });

export const deathQuery = (deathId: string) =>
  queryOptions({
    queryKey: ['deaths', 'detail', deathId],
    queryFn: () => api('GET', `/deaths/${deathId}`, { schema: deathRecordSchema }),
  });

export const deathRegisterQuery = queryOptions({
  queryKey: ['deaths', 'register'],
  queryFn: () => api('GET', '/deaths', { schema: deathListSchema }),
});

export const patientDeathsQuery = (patientId: string) =>
  queryOptions({
    queryKey: ['deaths', 'patient', patientId],
    queryFn: () => api('GET', `/patients/${patientId}/deaths`, { schema: patientDeathsSchema }),
  });

export const recordDeath = (visitId: string, body: In<typeof recordDeathBodySchema>) =>
  api('POST', `/visits/${visitId}/deaths`, { body, schema: deathRecordSchema });

export const requestDeathCorrection = (deathId: string, reason: string) =>
  api('POST', `/deaths/${deathId}/corrections`, {
    body: { clientRequestId: crypto.randomUUID(), reason },
    schema: deathRecordSchema,
  });

export const decideDeathCorrection = (
  correctionId: string,
  action: 'approve' | 'reject' | 'withdraw',
  note?: string,
) =>
  api('POST', `/death-corrections/${correctionId}/${action}`, {
    body: note === undefined ? {} : { note },
    schema: deathRecordSchema,
  });

export const deathPrintQuery = (deathId: string) =>
  queryOptions({
    queryKey: ['deaths', 'print', deathId],
    queryFn: () => api('GET', `/deaths/${deathId}/print`, { schema: deathPrintSchema }),
  });

export const markDeathPrinted = (deathId: string) =>
  api('POST', `/deaths/${deathId}/printed`, { schema: printCopySchema });

// --- Births ------------------------------------------------------------------------------

export const birthFormOptionsQuery = queryOptions({
  queryKey: ['births', 'form-options'],
  queryFn: () => api('GET', '/births/form-options', { schema: birthFormOptionsSchema }),
  staleTime: 5 * 60_000,
});

export const birthQuery = (birthId: string) =>
  queryOptions({
    queryKey: ['births', 'detail', birthId],
    queryFn: () => api('GET', `/births/${birthId}`, { schema: birthNotificationSchema }),
  });

export const birthRegisterQuery = queryOptions({
  queryKey: ['births', 'register'],
  queryFn: () => api('GET', '/births', { schema: birthListSchema }),
});

export const patientBirthsQuery = (patientId: string) =>
  queryOptions({
    queryKey: ['births', 'patient', patientId],
    queryFn: () => api('GET', `/patients/${patientId}/births`, { schema: patientBirthsSchema }),
  });

export const recordBirth = (visitId: string, body: In<typeof recordBirthBodySchema>) =>
  api('POST', `/visits/${visitId}/births`, { body, schema: birthNotificationSchema });

export const birthEnteredInError = (birthId: string, reason: string) =>
  api('POST', `/births/${birthId}/entered-in-error`, {
    body: { reason },
    schema: birthNotificationSchema,
  });

export const birthPrintQuery = (birthId: string) =>
  queryOptions({
    queryKey: ['births', 'print', birthId],
    queryFn: () => api('GET', `/births/${birthId}/print`, { schema: birthPrintSchema }),
  });

export const markBirthPrinted = (birthId: string) =>
  api('POST', `/births/${birthId}/printed`, { schema: printCopySchema });
